import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardFooter 
} from '@/components/ui/card';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Search, Clock, FileText, Calendar, X, Tag, CreditCard, Receipt, Utensils, User, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import OrderReprintButton from '@/components/orders/OrderReprintButton';

const CompletedTablesPage: React.FC = () => {
  const { orders, paymentMethods } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOrders, setFilteredOrders] = useState<any[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  const completedOrders = orders.filter(order => order.status === 'paid');
  
  useEffect(() => {
    let result = [...completedOrders];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(order => 
        order.id.toLowerCase().includes(term) ||
        order.tableId?.toString().toLowerCase().includes(term) ||
        order.totalAmount.toString().toLowerCase().includes(term)
      );
    }
    
    result.sort((a, b) => {
      const dateA = a.paymentDate ? new Date(a.paymentDate).getTime() : new Date(a.updatedAt).getTime();
      const dateB = b.paymentDate ? new Date(b.paymentDate).getTime() : new Date(b.updatedAt).getTime();
      return dateB - dateA;
    });
    
    setFilteredOrders(result);
  }, [searchTerm, completedOrders]);
  
  const getPaymentMethodName = (id?: string) => {
    if (!id) return 'N/A';
    const method = paymentMethods.find(m => m.id === id);
    return method ? method.name : 'N/A';
  };

  const handleViewDetails = (order: any) => {
    setSelectedOrder(order);
    setIsDetailsOpen(true);
  };

  const getOrderTypeDisplay = (order: any) => {
    if (order.type === 'dine-in' && order.tableId) {
      const tableNumber = order.tableId.split('-')[0];
      return `Mesa ${tableNumber}`;
    } else if (order.type === 'delivery') {
      return 'Delivery';
    } else {
      return 'Balcão';
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Pedidos Finalizados</h1>
      </div>
      
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input 
          placeholder="Pesquisar pedidos..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>
      
      {filteredOrders.length === 0 ? (
        <Card className="bg-gray-50">
          <CardContent className="pt-6 text-center text-muted-foreground">
            <p>Nenhum pedido finalizado encontrado.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredOrders.map(order => {
            const orderTypeDisplay = getOrderTypeDisplay(order);
            const icon = order.type === 'dine-in' ? 
              <Utensils className="h-3 w-3 mr-1" /> : 
              <Receipt className="h-3 w-3 mr-1" />;
            
            const hasCustomer = order.customer && (order.customer.name || order.customer.id);
            const customerName = hasCustomer ? order.customer.name : "Não Identificado";
            
            return (
              <Card key={order.id} className="overflow-hidden border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
                <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-transparent">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <span className="flex items-center">
                          {icon}
                          {orderTypeDisplay}
                        </span>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {getPaymentMethodName(order.paymentMethodId)}
                        </Badge>
                      </CardTitle>
                      <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatDate(order.paymentDate || order.updatedAt)}
                      </div>
                      
                      <div className="flex items-center text-sm mt-1">
                        <User className="h-3 w-3 mr-1 text-muted-foreground" />
                        <span>Cliente: {customerName}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground">Total</div>
                      <div className="font-bold text-green-700">{formatCurrency(order.totalAmount)}</div>
                      
                      {order.receivedAmount && (
                        <div className="text-xs mt-1">
                          <div className="text-muted-foreground">Recebido: {formatCurrency(order.receivedAmount)}</div>
                          <div className="text-green-600">Troco: {formatCurrency(order.changeAmount || 0)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pb-4">
                  <div className="text-sm">
                    <div className="flex justify-between items-center border-t pt-2 mt-2">
                      <span className="text-muted-foreground flex items-center">
                        <Tag className="h-3 w-3 mr-1" /> {order.items.length} {order.items.length === 1 ? 'item' : 'itens'}
                      </span>
                      <div className="flex items-center space-x-2">
                        <OrderReprintButton 
                          order={order}
                          size="sm"
                          variant="ghost"
                          className="h-7 px-3 text-green-600 hover:text-green-800 hover:bg-green-50"
                        />
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 px-3 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          onClick={() => handleViewDetails(order)}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Detalhes
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-[500px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Detalhes do Pedido</span>
              <Badge variant="outline" className="ml-2">
                {selectedOrder && getOrderTypeDisplay(selectedOrder)}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {selectedOrder && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {formatDate(selectedOrder.paymentDate || selectedOrder.updatedAt)}
                    </span>
                  </div>
                  <div className="font-medium">
                    ID: {selectedOrder.id.substring(0, 8)}
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <ScrollArea className="flex-1">
              <div className="space-y-4">
                <div className="bg-muted/30 p-3 rounded-md">
                  <h4 className="font-medium mb-2">Cliente</h4>
                  <div className="text-sm">
                    {selectedOrder.customer && (selectedOrder.customer.name || selectedOrder.customer.id) ? (
                      <div>
                        <p><strong>Nome:</strong> {selectedOrder.customer.name}</p>
                        {selectedOrder.customer.phone && (
                          <p><strong>Telefone:</strong> {selectedOrder.customer.phone}</p>
                        )}
                        {selectedOrder.customer.address && (
                          <p><strong>Endereço:</strong> {selectedOrder.customer.address}</p>
                        )}
                      </div>
                    ) : (
                      <p>Cliente não identificado</p>
                    )}
                  </div>
                </div>
                
                <div className="bg-muted/30 p-3 rounded-md">
                  <h4 className="font-medium mb-2">Informações do Pedido</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Status:</div>
                    <div className="font-medium">Finalizado</div>
                    
                    <div>Valor Total:</div>
                    <div className="font-medium">{formatCurrency(selectedOrder.totalAmount)}</div>
                    
                    {selectedOrder.discount > 0 && (
                      <>
                        <div>Desconto:</div>
                        <div className="font-medium text-green-600">
                          {selectedOrder.discountType === 'percentage' 
                            ? `${selectedOrder.discount}%` 
                            : formatCurrency(selectedOrder.discount)}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Forma de Pagamento</h4>
                  {selectedOrder.VendaFormaPagamento && selectedOrder.VendaFormaPagamento.length > 0 ? (
                    <div className="space-y-2">
                      {selectedOrder.VendaFormaPagamento.map((payment, index) => (
                        <div key={index} className="flex items-center justify-between p-2 border rounded-md bg-blue-50">
                          <span className="text-sm">{payment.FormaPagamentoNome}</span>
                          <span className="font-medium">{formatCurrency(payment.Valor)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center p-2 border rounded-md bg-blue-50">
                      <CreditCard className="h-4 w-4 mr-2 text-blue-600" />
                      <span>{getPaymentMethodName(selectedOrder.paymentMethodId)}</span>
                    </div>
                  )}
                  
                  {selectedOrder.receivedAmount > 0 && (
                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm p-2 border rounded-md">
                      <div>Valor Recebido:</div>
                      <div className="font-medium">{formatCurrency(selectedOrder.receivedAmount)}</div>
                      
                      <div>Troco:</div>
                      <div className="font-medium">{formatCurrency(selectedOrder.changeAmount || 0)}</div>
                    </div>
                  )}
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Itens do Pedido</h4>
                  <div className="space-y-2">
                    {selectedOrder.items.map((item: any, index: number) => (
                      <div 
                        key={index} 
                        className="p-2 border rounded-md flex justify-between items-center"
                      >
                        <div>
                          <div className="font-medium">{item.product.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {item.quantity} x {formatCurrency(item.product.price)}
                          </div>
                        </div>
                        <div className="font-medium">
                          {formatCurrency(item.quantity * item.product.price)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {selectedOrder.observations && (
                  <div>
                    <h4 className="font-medium mb-2">Observações</h4>
                    <div className="p-2 border rounded-md bg-amber-50 text-sm">
                      {selectedOrder.observations}
                    </div>
                  </div>
                )}
                
                {selectedOrder.document && (
                  <div>
                    <h4 className="font-medium mb-2">Documento do Cliente</h4>
                    <div className="p-2 border rounded-md bg-gray-50 text-sm font-mono">
                      {selectedOrder.document}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
          
          <div className="mt-2 pt-2 border-t flex justify-end gap-2">
            {selectedOrder && (
              <OrderReprintButton
                order={selectedOrder}
                className="gap-2"
              />
            )}
            <Button 
              variant="outline" 
              className="gap-2" 
              onClick={() => setIsDetailsOpen(false)}
            >
              <X className="h-4 w-4" />
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompletedTablesPage;
